import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Save, Send } from "lucide-react";
import { CampaignsPanel } from "@/components/admin/CampaignsPanel";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export function AutomationsPanel() {
  const [data, setData] = useState(null);
  const [segs, setSegs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, s] = await Promise.all([api.get("/admin/automations"), api.get("/admin/newsletter/segments")]);
      setData(a.data); setSegs(s.data);
    } catch { toast.error("Errore caricamento automazioni"); }
  };
  useEffect(() => { load(); }, []);

  const toggleAll = async (enabled) => {
    try { await api.put("/admin/automations/config", { enabled }); toast.success(enabled ? "Automazioni attivate" : "Automazioni in pausa"); load(); }
    catch { toast.error("Errore"); }
  };

  const saveTpl = async (key, tpl) => {
    setBusy(true);
    try { await api.put(`/admin/automations/templates/${key}`, tpl); toast.success("Template salvato"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const testTpl = async (key) => {
    try { const { data } = await api.post("/admin/automations/test", { template_key: key }); toast.success(`Email di prova inviata a ${data.to}`); }
    catch (err) { toast.error(err.response?.data?.detail || "Invio test non riuscito"); }
  };

  const runNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/automations/run"); toast.success(`Ciclo eseguito: ${data.sent} email inviate`); load(); }
    catch { toast.error("Errore esecuzione"); }
    finally { setBusy(false); }
  };

  const toggleWeeklyReport = async (enabled) => {
    try { await api.put("/admin/settings", { weekly_report_enabled: enabled }); toast.success(enabled ? "Report settimanale attivato" : "Report settimanale disattivato"); load(); }
    catch { toast.error("Errore salvataggio"); }
  };

  const sendWeeklyNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/weekly-report/send-now"); toast.success(`Report inviato a ${data.to}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Invio report non riuscito"); }
    finally { setBusy(false); }
  };

  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  if (!data) return <p className="text-taupe text-sm">Caricamento…</p>;

  return (
    <div className="space-y-8" data-testid="automations-panel">
      {/* Newsletter manuali: si aggiungono alle automazioni, non le sostituiscono */}
      <CampaignsPanel />
      <div className="border-t border-hair pt-8">
        <div className="eyebrow mb-1">Automazioni attive</div>
        <p className="text-xs text-taupe">Le sequenze automatiche esistenti (benvenuto, follow-up Library, promemoria Shopping List) continuano a funzionare invariate.</p>
      </div>
      {/* Stato globale + segmenti + report settimanale */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        <div className="bg-white border border-hair p-5 md:p-6">
          <div className="eyebrow mb-3">Stato automazioni</div>
          <div className="flex items-center gap-3">
            <Switch checked={data.enabled} onCheckedChange={toggleAll} data-testid="automations-enabled" />
            <span className="text-sm">{data.enabled ? "Attive — il sistema controlla ogni 30 minuti" : "In pausa — nessun invio automatico"}</span>
          </div>
          <button onClick={runNow} disabled={busy} className={`${btn} border border-ink hover:bg-ink hover:text-bone mt-4`} data-testid="automations-run"><Play size={14} /> Esegui ora il ciclo</button>
          <p className="text-[11px] text-taupe mt-3">Le email marketing partono SOLO verso contatti con consenso marketing valido. Ogni email contiene il link di disiscrizione.</p>
        </div>
        <div className="bg-white border border-hair p-5 md:p-6" data-testid="weekly-report-box">
          <div className="eyebrow mb-3">Report settimanale via email</div>
          <div className="flex items-center gap-3">
            <Switch checked={data.weekly_report?.enabled ?? true} onCheckedChange={toggleWeeklyReport} data-testid="weekly-report-enabled" />
            <span className="text-sm">{data.weekly_report?.enabled ?? true ? "Attivo — ogni lunedì mattina" : "Disattivato"}</span>
          </div>
          <p className="text-[11px] text-taupe mt-3">
            Riepilogo automatico alla proprietaria: visualizzazioni, sessioni, sorgenti di traffico, pagine più viste e attività
            (prenotazioni, ordini, lead). Solo traffico reale, test esclusi. Ultimo invio: <span data-testid="weekly-report-last">{data.weekly_report?.last_sent || "mai"}</span>
          </p>
          <button onClick={sendWeeklyNow} disabled={busy} className={`${btn} border border-ink hover:bg-ink hover:text-bone mt-4`} data-testid="weekly-report-send"><Send size={14} /> Invia ora una copia</button>
        </div>
        <div className="bg-white border border-hair p-5 md:p-6" data-testid="segments-box">
          <div className="eyebrow mb-3">Segmenti newsletter (consenso marketing)</div>
          <p className="text-sm">{segs?.marketing_total ?? 0} contatti con consenso · {segs?.newsletter_active ?? 0} iscritti newsletter attivi</p>
          <div className="mt-3 space-y-1.5">
            {(segs?.segments || []).map((s) => (
              <div key={s.name} className="flex justify-between text-xs text-taupe border-b border-hair/60 pb-1.5">
                <span className="capitalize">{s.name}</span><span>{s.count}</span>
              </div>
            ))}
            {!(segs?.segments || []).length && <p className="text-xs text-taupe">Nessun segmento ancora: i segmenti si creano automaticamente dalle categorie dei Book scaricati.</p>}
          </div>
        </div>
      </div>

      {/* Template */}
      <div className="space-y-4" data-testid="templates-list">
        {Object.entries(data.templates).map(([key, tpl]) => (
          <TemplateEditor key={key} tkey={key} tpl={tpl} meta={data.meta[key] || {}} onSave={saveTpl} onTest={testTpl} busy={busy} btn={btn} />
        ))}
      </div>

      {/* Log invii */}
      <div className="bg-white border border-hair p-5 md:p-6" data-testid="automations-log">
        <div className="eyebrow mb-3">Ultimi invii automatici</div>
        {(data.log || []).slice(0, 20).map((l, i) => (
          <p key={i} className="text-xs text-taupe py-1.5 border-b border-hair/60 last:border-0">
            {fmtDate(l.at)} — {l.type} → {l.to} · <span className={l.result === "inviata" ? "text-emerald-700" : "text-amber-700"}>{l.result}</span>
          </p>
        ))}
        {!(data.log || []).length && <p className="text-xs text-taupe">Nessun invio automatico ancora registrato.</p>}
      </div>
    </div>
  );
}

function TemplateEditor({ tkey, tpl, meta, onSave, onTest, busy, btn }) {
  const [form, setForm] = useState({ subject: tpl.subject, body: tpl.body, enabled: tpl.enabled, delay_hours: tpl.delay_hours ?? meta.delay_hours ?? 72 });
  useEffect(() => { setForm({ subject: tpl.subject, body: tpl.body, enabled: tpl.enabled, delay_hours: tpl.delay_hours ?? meta.delay_hours ?? 72 }); }, [tpl]);
  return (
    <div className="bg-white border border-hair p-5 md:p-6 space-y-4" data-testid={`template-${tkey}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">{meta.label || tkey}</div>
          <p className="text-[11px] text-taupe mt-1">Variabili disponibili: {"{book_title}"} {"{project_name}"}{meta.cta === "stima" ? " · include il pulsante “Calcola la tua stima gratuita”" : ""}</p>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid={`template-enabled-${tkey}`} />
          <span className="text-xs text-taupe">{form.enabled ? "Attiva" : "Disattivata"}</span>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2"><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Oggetto</Label>
          <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-none mt-1" data-testid={`template-subject-${tkey}`} /></div>
        {meta.delay_hours != null && (
          <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Invio dopo (ore)</Label>
            <Input type="number" min={1} value={form.delay_hours} onChange={(e) => setForm({ ...form, delay_hours: Number(e.target.value) })} className="rounded-none mt-1" data-testid={`template-delay-${tkey}`} /></div>
        )}
      </div>
      <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Testo (HTML semplice consentito)</Label>
        <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-none mt-1 font-mono text-xs" data-testid={`template-body-${tkey}`} /></div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => onSave(tkey, form)} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid={`template-save-${tkey}`}><Save size={14} /> Salva</button>
        <button onClick={() => onTest(tkey)} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid={`template-test-${tkey}`}><Send size={14} /> Invia prova a me</button>
      </div>
    </div>
  );
}
